import React, { type ComponentType } from "react";
import type { BaseProductSet, TripsListProps } from "@b2bportal/core-trips";
import { CallState, type TripProduct } from "@b2bportal/core-types";
import { sortTripsByDate } from "../utils/sortByDate";

export const useTripsList = <Products extends BaseProductSet>(
  props: TripsListProps<Products>
) => {
  const { products } = props;

  const productsList = React.useMemo(() => {
    return Object.values(products);
  }, [products]);

  React.useEffect(() => {
    for (const product of productsList) {
      product.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isLoading, hasError } = React.useMemo(
    () =>
      productsList.reduce(
        (prev, product) => {
          if ("status" in product.reservations) {
            return {
              isLoading: prev.isLoading || product.reservations.isLoading,
              hasError: prev.hasError || product.reservations.isError,
            };
          } else {
            return {
              isLoading:
                prev.isLoading ||
                product.reservations.state === CallState.InProcess,
              hasError:
                prev.hasError ||
                product.reservations.state === CallState.Failed,
            };
          }
        },
        {
          isLoading: false,
          hasError: false,
        }
      ),
    [productsList]
  );

  // Stable per-reservation renderer cache.
  //
  // React reconciles by component *type* (the function reference). If we build
  // a fresh component for each reservation on every render (as a naive
  // `component: (props) => <product.component .../>` does), then every time the
  // upstream product objects churn — which RTK Query does frequently even when
  // the underlying data is unchanged — React sees new component types and
  // unmounts/remounts the entire list. That tears down and rebuilds the DOM
  // constantly, which resets assistive technology (the screen reader loses its
  // place and re-announces the whole list).
  //
  // To avoid that we cache ONE stable renderer per reservationId (kept in a
  // ref so it survives re-renders) and feed it the latest reservation through a
  // mutable holder. The component type stays constant across renders, so React
  // reconciles and only updates props — no remount, no AT churn.
  const holders = React.useRef(
    new Map<string, { reservation: unknown; render: ComponentType }>()
  );

  const trips = React.useMemo(() => {
    const activeIds = new Set<string>();

    const wrappers = productsList.flatMap(function <Reservation>(
      product: TripProduct<Reservation>
    ) {
      return (
        product.reservations.data?.map((wrapper) => {
          const id = wrapper.reservationId;
          activeIds.add(id);

          const existing = holders.current.get(id);
          if (existing) {
            // Keep the stable renderer; just refresh the data it reads.
            existing.reservation = wrapper.reservation;
            return { ...wrapper, component: existing.render };
          }

          const holder: { reservation: Reservation; render: ComponentType } = {
            reservation: wrapper.reservation,
            render: () => (
              <product.component reservation={holder.reservation} />
            ),
          };
          holders.current.set(id, holder);
          return { ...wrapper, component: holder.render };
        }) ?? []
      );
    });

    // Drop renderers for reservations that no longer exist.
    for (const id of [...holders.current.keys()]) {
      if (!activeIds.has(id)) {
        holders.current.delete(id);
      }
    }

    return sortTripsByDate(wrappers);
  }, [productsList]);

  return {
    context: { trips, hasError, isLoading },
  };
};
